import React, { useEffect, useState } from "react";
import { AppLayout } from "../shared/layout";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { changePassword, deleteMyAccount, getPreferences, updatePreferences } from "../../../data";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import AuthStore from "../../../stores/AuthStore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export function SettingsScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [notifications, setNotifications] = useState({
    notificationEmailEnabled: true,
    notificationPushEnabled: true,
    notificationMarketing: true,
  });
  const [practiceVoice, setPracticeVoice] = useState(
    () => localStorage.getItem("pairvioRealtimeVoice") || "marin"
  );

  const { data: preferencesData } = useQuery({
    queryKey: ["preferences"],
    queryFn: getPreferences,
  });

  useEffect(() => {
    const data = preferencesData?.data;
    if (data) {
      setNotifications({
        notificationEmailEnabled: data.notificationEmailEnabled ?? true,
        notificationPushEnabled: data.notificationPushEnabled ?? true,
        notificationMarketing: data.notificationMarketing ?? true,
      });
    }
  }, [preferencesData]);

  const changePasswordMutation = useMutation({
    mutationFn: changePassword,
    onSuccess: () => {
      toast({
        title: t("common.success"),
        description: t("settings.password.success"),
        variant: "success",
      });
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description: error.response?.data?.message || t("settings.password.failed"),
        variant: "destructive",
      });
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => deleteMyAccount("web settings delete"),
    onSuccess: () => {
      AuthStore.logout();
      navigate("/login");
      toast({
        title: t("settings.delete.successTitle"),
        description: t("settings.delete.successDescription"),
      });
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description: error.response?.data?.message || t("settings.delete.failed"),
        variant: "destructive",
      });
    },
  });

  const preferencesMutation = useMutation({
    mutationFn: updatePreferences,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["preferences"] });
      toast({
        title: t("settings.notifications.updatedTitle"),
        description: t("settings.notifications.updatedDescription"),
      });
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description: error.response?.data?.message || t("settings.notifications.failed"),
        variant: "destructive",
      });
    },
  });

  const handlePasswordChange = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: t("common.error"),
        description: t("settings.password.mismatch"),
        variant: "destructive",
      });
      return;
    }
    changePasswordMutation.mutate({
      oldPassword: passwordData.currentPassword,
      newPassword: passwordData.newPassword,
    });
  };

  const handleNotificationChange = (key) => {
    const next = { ...notifications, [key]: !notifications[key] };
    setNotifications(next);
    preferencesMutation.mutate(next);
  };

  const handleVoiceChange = (event) => {
    const nextVoice = event.target.value === "cedar" ? "cedar" : "marin";
    setPracticeVoice(nextVoice);
    localStorage.setItem("pairvioRealtimeVoice", nextVoice);
  };

  return (
    <AppLayout
      title={t("settings.title")}
      description={t("settings.description")}
    >
      <div className="space-y-8 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.voice.title")}</CardTitle>
            <CardDescription>{t("settings.voice.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            <Label htmlFor="practiceVoice">{t("settings.voice.label")}</Label>
            <select
              id="practiceVoice"
              value={practiceVoice}
              onChange={handleVoiceChange}
              className="mt-2 flex h-10 w-full max-w-sm rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="marin">{t("settings.voice.marin")}</option>
              <option value="cedar">{t("settings.voice.cedar")}</option>
            </select>
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card>
          <CardHeader>
            <CardTitle>{t("settings.notifications.title")}</CardTitle>
            <CardDescription>
              {t("settings.notifications.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>{t("settings.notifications.emailTitle")}</Label>
                <p className="text-sm text-gray-500">
                  {t("settings.notifications.emailDescription")}
                </p>
              </div>
              <Switch
                checked={notifications.notificationEmailEnabled}
                onCheckedChange={() => handleNotificationChange("notificationEmailEnabled")}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>{t("settings.notifications.pushTitle")}</Label>
                <p className="text-sm text-gray-500">
                  {t("settings.notifications.pushDescription")}
                </p>
              </div>
              <Switch
                checked={notifications.notificationPushEnabled}
                onCheckedChange={() => handleNotificationChange("notificationPushEnabled")}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>{t("settings.notifications.marketingTitle")}</Label>
                <p className="text-sm text-gray-500">
                  {t("settings.notifications.marketingDescription")}
                </p>
              </div>
              <Switch
                checked={notifications.notificationMarketing}
                onCheckedChange={() => handleNotificationChange("notificationMarketing")}
              />
            </div>
          </CardContent>
        </Card>

        {/* Password Change */}
        {!AuthStore.user?.googleId && (
          <Card>
            <CardHeader>
              <CardTitle>{t("settings.password.title")}</CardTitle>
              <CardDescription>
                {t("settings.password.description")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">{t("settings.password.current")}</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">{t("settings.password.new")}</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{t("settings.password.confirm")}</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                      required
                      minLength={6}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={changePasswordMutation.isPending || changePasswordMutation.isLoading}>
                    {(changePasswordMutation.isPending || changePasswordMutation.isLoading) ? t("settings.password.updating") : t("settings.password.action")}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Danger Zone */}
        <Card className="border-red-100">
          <CardHeader>
            <CardTitle className="text-red-600">{t("settings.delete.dangerTitle")}</CardTitle>
            <CardDescription>
              {t("settings.delete.dangerDescription")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium">{t("settings.delete.title")}</h4>
                <p className="text-sm text-gray-500">
                  {t("settings.delete.description")}
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">{t("settings.delete.action")}</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("settings.delete.confirmTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t("settings.delete.confirmDescription")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteAccountMutation.mutate()}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {(deleteAccountMutation.isPending || deleteAccountMutation.isLoading) ? t("settings.delete.deleting") : t("settings.delete.confirmAction")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
